import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { usePageContext } from 'application/contexts/PageContext';
import { useSiteContext } from 'application/contexts/SiteContext';
import { PublicationPage } from 'designsystem/components/5-templates/PublicationPage';
import { ConvertToPictureObject } from 'designsystem/utilities/convertToPictureObject';
import { CludoLang } from 'features/Layouts/Layout/Vendors/Cludo';
import { AsideFeature } from 'features/Modules/Aside';
import { SingleContentSectionFeature } from 'features/Modules/ContentSection/SingleContentSectionFeature';
import { ModuleFeature } from 'features/Modules/DynamicModule';
import { RelatedListFeature } from 'features/Modules/RelatedList';
import { TopBannerFeature } from 'features/Modules/TopBanner';
import { getArticleGroupId } from 'helpers/getArticleGroupId';
import Head from 'next/head';
import { useRouter } from 'next/router';

export const PublicationPageFeature: React.FC<Content.PublicationPage> = ({ properties, additionalTop, additionalPageContent }) => {
	const {
		asideContent,
		bannerContent,
		date,
		hideRelatedPages = false,
		image: oldImage,
		pdf,
		pageSection,
		pageSections,
		picture,
		tags,
		text,
	} = properties ?? {};
	const pictureObject = ConvertToPictureObject(picture);

	const dictionary = useDictionaryContext();
	const siteContext = useSiteContext();
	const pageContext = usePageContext();
	const router = useRouter();
	const lang = router.locale.split('-')[0] as CludoLang;
	const { theme } = siteContext ?? {};

	const dateWithoutTime = date.split('T')[0];

	const downloadText = `${dictionary.getAction('Get')} 
						${dictionary.getLabel('Publication').toLowerCase()} 
						${dictionary.getExplainer('AsPDF').toLowerCase()}`;

	const url = pictureObject !== null ? pictureObject.url : oldImage?.[0].url;
	const focalPoint = pictureObject !== null ? pictureObject.focalPoint : oldImage?.[0].focalPoint;
	const altText = pictureObject !== null ? pictureObject.altText : oldImage?.[0].properties.altText;
	const altTextChecked = altText == '""' ? '' : altText;

	const groupId = getArticleGroupId({ theme, id: pageContext.parent?.id, locale: lang });

	return (
		<>
			<Head>
				<meta name="ArticleTypes" content="Publication" />
				<meta name="ArticleTags" content={tags?.map((tag) => tag.name).join('|')} />
				<meta name="ArticleGroupId" content={groupId} />
				<meta name="ArticleImage" content={url} />

				<meta name="ArticleDate" content={dateWithoutTime ? dateWithoutTime : null} />
				<meta property="article:published_time" content={dateWithoutTime} />
				<meta name="dc.date.created" content={dateWithoutTime} />
				<meta name="dcterms.created" content={dateWithoutTime} />
				<meta name="pubDate" content={dateWithoutTime.replaceAll('-', '')} />
			</Head>
			<PublicationPage
				additionalTop={additionalTop}
				additionalPageContent={additionalPageContent}
				aside={
					<AsideFeature>
						{asideContent?.items?.map((pageSection, index) => (
							<ModuleFeature key={index} contentModule={pageSection} />
						))}
						{!hideRelatedPages && (
							<RelatedListFeature
								parentId={pageContext.parent?.id}
								tags={tags?.map((tag) => tag.name)}
								heading={dictionary.getLabel('RelatedPublications')}
							/>
						)}
					</AsideFeature>
				}
				{...properties}
				text={text?.markup}
				image={
					url && {
						altText: altTextChecked,
						url,
						focalPoint,
					}
				}
				date={dateWithoutTime}
				downloadText={downloadText}
				pdf={{ url: pdf?.[0].url, bytes: pdf?.[0]?.bytes }}
				topBanner={bannerContent?.items?.length > 0 && <TopBannerFeature bannerContent={bannerContent} />}
			>
				<SingleContentSectionFeature pageSection={pageSection?.items} noBackground />
				{pageSections?.items?.map((pageSectionItems, index) => (
					<SingleContentSectionFeature
						key={index + 'PageSection'}
						pageSection={pageSectionItems.content.properties.content?.items}
						noBackground
					/>
				))}
			</PublicationPage>
		</>
	);
};
